import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TrackCard = ({ track, isExpanded, onToggle, onCompare, isInComparison }) => {
  const navigate = useNavigate();

  const getDifficultyColor = (level) => {
    switch (level) {
      case 'Élevé': return 'text-red-700 bg-red-50 border-red-100';
      case 'Moyen': return 'text-amber-700 bg-amber-50 border-amber-100';
      case 'Accessible': return 'text-green-700 bg-green-50 border-green-100';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const handleExplore = () => {
    // Redirige vers la base de données en pré-filtrant par domaine
    navigate('/post-bac-database', { 
      state: { 
        search: track?.name.split(' ')[0], // Cherche "Sciences", "Lettres", etc.
        source: 'lycee-orientation'
      } 
    });
  };

  return (
    <div className={`bg-card border border-border rounded-lg shadow-sm transition-all duration-300 ${
      isExpanded ? 'ring-2 ring-primary/20 shadow-md' : 'hover:shadow-md'
    }`}>
      {/* Card Header - Toujours visible */}
      <div 
        className="p-5 cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-sm ${track?.color}`}>
              <Icon name={track?.icon} size={24} color="white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-foreground">{track?.name}</h3>
              <p className="text-sm text-muted-foreground mt-0.5 line-clamp-1">{track?.shortDescription}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
             {/* Badge Difficulté */}
            <span className={`hidden sm:inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${getDifficultyColor(track?.difficulty)}`}>
              {track?.difficulty}
            </span>
            
            <button
              className="p-2 hover:bg-muted rounded-full transition-colors"
              aria-label={isExpanded ? 'Réduire' : 'Développer'}
            >
              <Icon 
                name={isExpanded ? 'ChevronUp' : 'ChevronDown'} 
                size={20} 
                className="text-muted-foreground"
              />
            </button>
          </div>
        </div>
      </div>

      {/* Expanded Content - Visible au clic */}
      {isExpanded && (
        <div className="px-6 pb-6 pt-2 space-y-6 animate-fade-in border-t border-border/50">
          
          {/* Badge Difficulté (Mobile seulement) */}
          <div className="sm:hidden mb-4">
             <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${getDifficultyColor(track?.difficulty)}`}>
              Difficulté : {track?.difficulty}
            </span>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Colonne Gauche : Académique */}
            <div className="space-y-6">
                 {/* Subjects */}
                <div>
                    <h4 className="text-sm font-semibold uppercase text-muted-foreground mb-3 flex items-center tracking-wider">
                    <Icon name="BookOpen" size={14} className="mr-2" />
                    Matières Dominantes
                    </h4>
                    <div className="flex flex-wrap gap-2">
                    {track?.subjects?.map((subject, index) => (
                        <span 
                        key={index}
                        className="px-2.5 py-1.5 bg-muted text-foreground rounded-md text-xs font-medium"
                        >
                        {subject}
                        </span>
                    ))}
                    </div>
                </div>

                {/* Specializations */}
                <div>
                    <h4 className="text-sm font-semibold uppercase text-muted-foreground mb-3 flex items-center tracking-wider">
                    <Icon name="Target" size={14} className="mr-2" />
                    Branches & Options
                    </h4>
                    <ul className="space-y-2">
                    {track?.specializations?.map((spec, index) => (
                        <li key={index} className="flex items-start text-sm text-foreground">
                        <span className="w-1.5 h-1.5 bg-primary rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                        {spec}
                        </li>
                    ))}
                    </ul>
                </div>
            </div>

            {/* Colonne Droite : Avenir & Stats */}
            <div className="space-y-6">
                {/* Career Prospects */}
                <div>
                    <h4 className="text-sm font-semibold uppercase text-muted-foreground mb-3 flex items-center tracking-wider">
                    <Icon name="Briefcase" size={14} className="mr-2" />
                    Débouchés possibles
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {track?.careerProspects?.map((career, index) => (
                        <div key={index} className="flex items-center space-x-2 text-sm text-foreground">
                        <Icon name="Check" size={12} className="text-green-600 flex-shrink-0" />
                        <span>{career}</span>
                        </div>
                    ))}
                    </div>
                </div>

                {/* Statistics */}
                <div className="bg-muted/30 rounded-xl p-4 border border-border/50">
                    <div className="grid grid-cols-2 gap-4 text-center">
                        <div>
                            <div className="text-xl font-bold text-primary">{track?.statistics?.successRate}</div>
                            <div className="text-xs text-muted-foreground uppercase font-medium mt-1">Taux Réussite</div>
                        </div>
                        <div>
                            <div className="text-xl font-bold text-secondary">{track?.statistics?.employmentRate}</div>
                            <div className="text-xs text-muted-foreground uppercase font-medium mt-1">Insertion Pro</div>
                        </div>
                    </div>
                    <div className="text-[10px] text-center text-muted-foreground mt-2 italic">
                        * Estimations basées sur les tendances nationales
                    </div>
                </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 mt-2">
            <Button 
              variant="default" 
              onClick={handleExplore}
              iconName="Search"
              iconPosition="left"
              className="flex-1"
            >
              Voir les écoles compatibles
            </Button>
            <Button 
              variant={isInComparison ? "secondary" : "outline"}
              onClick={(e) => { e.stopPropagation(); onCompare(track); }}
              iconName={isInComparison ? "Check" : "GitCompare"}
              iconPosition="left"
              className={isInComparison ? "bg-green-100 text-green-700 border-green-200 hover:bg-green-200" : ""}
            >
              {isInComparison ? 'Ajouté au comparateur' : 'Comparer cette filière'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackCard;